import { useQueryClient } from "@tanstack/react-query"
import { useUser } from "api/ApiHooks"
import { LoversApi } from "api/LoversApi"
import { AxiosError } from "axios"
import { ArrowLeftEndOnRectangleIcon } from "@heroicons/react/24/outline"
import { UniqueGradient } from "components/UniqueGradient"
import { Button, buttonClasses, TextField, ThemeToggle } from "components/ui"
import { useEffect, useRef, useState } from "react"
import { classNames } from "utils/ClassNames"

export const ProfilePage = () => {
  return (
    <>
      <ProfileForm />
      <PasswordUpdateForm />
      <PreferencesSection />
      <AccountSection />
    </>
  )
}

const PreferencesSection = () => {
  return (
    <div className="space-y-12 mt-12">
      <div className="border-b border-gray-900/10 dark:border-white/10 pb-6">
        <h2 className="text-base/7 font-semibold text-gray-900 dark:text-white">
          Appearance
        </h2>
        <p className="mt-1 text-sm/6 text-gray-600 dark:text-gray-300">
          Choose how Lovers Live Forever looks to you.
        </p>
        <div className="mt-6 max-w-xs">
          <ThemeToggle />
        </div>
      </div>
    </div>
  )
}

const AccountSection = () => {
  return (
    <div className="space-y-12 mt-12">
      <div className="pb-6">
        <h2 className="text-base/7 font-semibold text-gray-900 dark:text-white">
          Account
        </h2>
        <p className="mt-1 text-sm/6 text-gray-600 dark:text-gray-300">
          Sign out of your account on this device.
        </p>
        <div className="mt-6">
          {/* Anchor (not a router Link) so the server can clear the auth cookies. */}
          <a href="/logout" className={buttonClasses({ variant: "secondary" })}>
            <ArrowLeftEndOnRectangleIcon
              aria-hidden="true"
              className="-ml-0.5 mr-2 h-5 w-5"
            />
            Logout
          </a>
        </div>
      </div>
    </div>
  )
}

const EmailSection = (props: { email?: string; isVerified?: boolean }) => {
  const [isResending, setIsResending] = useState(false)
  const [hasResentVerification, setHasResentVerification] = useState(false)

  const resendVerificationEmail = async () => {
    setIsResending(true)
    LoversApi()
      .verifyEmail.resendVerificationEmail()
      .then(() => {
        setHasResentVerification(true)
      })
      .finally(() => {
        setIsResending(false)
      })
  }

  return (
    <div className="sm:col-span-3">
      <p className="block text-sm/6 font-medium text-gray-900 dark:text-white">Email</p>
      <div className="mt-3 flex gap-2">
        <p>{props.email}</p>
        {props.isVerified ? (
          <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800 dark:bg-green-900/40 dark:text-green-300">
            Verified
          </span>
        ) : (
          <span className="inline-flex items-center rounded-full bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300">
            Not verified
          </span>
        )}
      </div>

      {(isResending || hasResentVerification) && (
        <p
          className={classNames(
            "mt-2 text-sm",
            isResending
              ? "text-gray-400 dark:text-gray-500 hover:text-gray-400 dark:hover:text-gray-500 hover:no-underline"
              : "",
            hasResentVerification
              ? "hover:text-green-600 text-green-600 hover:no-underline"
              : ""
          )}
        >
          {isResending ? "Resending..." : "Verification email resent!"}
        </p>
      )}

      {!props.isVerified && !isResending && !hasResentVerification && (
        <p
          onClick={resendVerificationEmail}
          className="mt-2 text-sm text-primary-600 hover:text-primary-500 hover:underline cursor-pointer"
        >
          Resend verification email
        </p>
      )}
    </div>
  )
}

const ProfileForm = () => {
  const { data } = useUser()
  const [isSaving, setIsSaving] = useState(false)
  const [hasSaved, setHasSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [name, setName] = useState(data?.user.name)
  const [photo, setPhoto] = useState<File | undefined>(undefined)
  const [photoPreview, setPhotoPreview] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()

  useEffect(() => {
    return () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview)
    }
  }, [photoPreview])

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      setPhoto(e.target.files[0])
      setPhotoPreview(URL.createObjectURL(e.target.files[0]))
    }
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsSaving(true)
    setError(null)

    try {
      const formData: {
        name?: string
        profileImage?: File
      } = {}
      if (name !== data?.user.name) formData.name = name
      if (photo) formData.profileImage = photo
      if (Object.keys(formData).length <= 0) return

      LoversApi()
        .self.updateSelf(formData)
        .then(() => {
          setHasSaved(true)
          queryClient.invalidateQueries({ queryKey: ["useUser"] })
        })
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong")
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="space-y-12">
        <div className="border-b border-gray-900/10 dark:border-white/10 pb-6">
          <h2 className="text-base/7 font-semibold text-gray-900 dark:text-white">
            Profile
          </h2>
          <p className="mt-1 text-sm/6 text-gray-600 dark:text-gray-300">
            Here you can edit the info about you. Updates will affect previous, current
            and future time capsule entries.
          </p>

          <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
            <div className="col-span-full">
              <label
                htmlFor="photo"
                className="block text-sm/6 font-medium text-gray-900 dark:text-white"
              >
                Photo
              </label>
              <div className="mt-2 flex items-center gap-x-3">
                {data?.user.profileImageUrl ? (
                  <img
                    src={photoPreview || data.user.profileImageUrl}
                    alt=""
                    className="h-10 w-10 rounded-full bg-gray-50 dark:bg-gray-800"
                  />
                ) : (
                  <UniqueGradient
                    id={data?.user.id}
                    className="h-10 w-10 rounded-full bg-gray-50 dark:bg-gray-800"
                  />
                )}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handlePhotoChange}
                  accept="image/*"
                  className="hidden"
                />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Change
                </Button>
              </div>
            </div>

            <div className="sm:col-span-3">
              <TextField
                label="Name"
                id="name"
                name="name"
                type="text"
                autoComplete="name"
                defaultValue={data?.user.name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <EmailSection
              email={data?.user.email}
              isVerified={data?.user.emailVerified}
            />
          </div>

          <div className="mt-6 flex items-center justify-end gap-x-6">
            <div>
              {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
              {hasSaved && (
                <p className="text-green-600 text-sm mt-2">
                  Profile updated successfully!
                </p>
              )}
            </div>
            <Button
              type="submit"
              isLoading={isSaving}
              disabled={name === data?.user.name}
            >
              {isSaving ? "Saving..." : "Save changes"}
            </Button>
          </div>
        </div>
      </div>
    </form>
  )
}

const PasswordUpdateForm = () => {
  const [isSaving, setIsSaving] = useState(false)
  const [hasSaved, setHasSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [newPassword, setNewPassword] = useState("")
  const [repeatNewPassword, setRepeatNewPassword] = useState("")
  const [currentPassword, setCurrentPassword] = useState("")

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsSaving(true)

    if (newPassword !== repeatNewPassword) {
      setError("New passwords do not match")
      setIsSaving(false)
      return
    }

    LoversApi()
      .self.updateSelfPassword({
        newPassword,
        currentPassword
      })
      .then(() => {
        setHasSaved(true)
      })
      .catch((err) => {
        if (err instanceof AxiosError) {
          switch (err.response?.data?.errorCode) {
            case "WRONG_PASSWORD":
              setError("Current password is incorrect")
              break
            default:
              setError(err.response?.data?.errorCode || "Something went wrong")
          }
        } else {
          setError(err instanceof Error ? err.message : "Something went wrong")
        }
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="space-y-12 mt-12">
        <div className="border-b border-gray-900/10 dark:border-white/10 pb-6">
          <h2 className="text-base/7 font-semibold text-gray-900 dark:text-white">
            Password
          </h2>
          <p className="mt-1 text-sm/6 text-gray-600 dark:text-gray-300">
            Update your password. Make sure it's at least 6 characters long.
          </p>

          <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
            <div className="sm:col-span-3">
              <TextField
                label="New Password"
                id="new-password"
                name="new-password"
                type="password"
                autoComplete="new-password"
                minLength={6}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>

            <div className="sm:col-span-3">
              <TextField
                label="Repeat New Password"
                id="repeat-new-password"
                name="repeat-new-password"
                type="password"
                autoComplete="new-password"
                minLength={6}
                onChange={(e) => setRepeatNewPassword(e.target.value)}
              />
            </div>

            <div className="sm:col-span-3">
              <TextField
                label="Current Password"
                id="current-password"
                name="current-password"
                type="password"
                autoComplete="current-password"
                minLength={6}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
            </div>
          </div>
          <div className="mt-6 flex items-center justify-end gap-x-6">
            <div>
              {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
              {hasSaved && (
                <p className="text-green-600 text-sm mt-2">
                  Password updated successfully!
                </p>
              )}
            </div>
            <Button
              type="submit"
              isLoading={isSaving}
              disabled={!newPassword || !repeatNewPassword || !currentPassword}
            >
              {isSaving ? "Updating password..." : "Update password"}
            </Button>
          </div>
        </div>
      </div>
    </form>
  )
}
